import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Avatar from "react-avatar";
import { Tooltip } from "@mui/material";
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import ElipsisText from "../../../../../components/ElipsisText";
import ElipsisCell from "../../../../../components/TableBox/Cells/EllipsisCell";
import Link from "../../../../../components/Link";
import Badge from "../../../../../components/Badge";
import Button from "../../../../../components/Button";
import { RowClassName, Modules, AuthRoutes, RouteParamsTypes, PermissionModulesEnum, PermissionActionsEnum } from "../../../../../enums";
import { TableContext } from "../../../../../contexts/TableContext";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { hasResourseAccess } from "../../../../../utils/methods";
import NoPropertyImg from '../../../../../assets/img/custom/no-property.jpeg';

interface RowProps {
  item: any;
  className?: RowClassName;
}

const Row: React.FC<RowProps> = ({ item, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Property]: [PermissionActionsEnum.Update] }] })

  const handleEditButtonClick = (id: string) => {
    navigate(`${Modules.resources}${AuthRoutes.properties}${RouteParamsTypes.edit}/${id}`);
  }
  const handleViewClick = (id: string) => {
    navigate(`${Modules.resources}${AuthRoutes.properties}${RouteParamsTypes.view}/${id}`);
  }

  const propertyNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 42 }}>
        <Avatar src={item.picture_url || NoPropertyImg} name={item.name} size="40" maxInitials={2} round />
      </div>
      <div style={{ width: 'calc(100% - 52px)' }}>
        <ElipsisText
          width="100%"
          text={item.name || '-'}
          node={
            <Link
              text={item.name || '-'}
              href={`${Modules.resources}${AuthRoutes.properties}${RouteParamsTypes.view}/${item.id}`}
              onClick={() => handleViewClick(item.id)}
            />
          }
          className="h6 mb-1 text-decoration-underline hyperlink"
        />
        <ElipsisText width="100%" text={item?.city_data?.name || '-'} />
      </div>
    </div>
  )

  const managedByNode = (
    <div>
      <ElipsisText width="100%" text={item?.manager?.name ?? '-'}/>
    </div>
  )

  const customerNode = (
    <div>
      <ElipsisText width="100%" text={item?.customer?.name ?? '-'} className="h6 mb-1" />
      <ElipsisText width="100%" text={item?.customer?.email ?? '-'} />
    </div>
  )

  return (
    <tr className={className}>
      {
        headCells.map((headCell) => {
          if (headCell.id === VALIDATION_FIELD_MAPPING.name) {
            return (
              <ElipsisCell
                key={headCell.id}
                width={headCell.width}
                node={propertyNode}
              />
            )
          }

          if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
            return (
              <td key={headCell.id} width={headCell.width}>
                <Badge
                  text={item.is_active ? t("property.list.pageTexts.table.status.active") : t("property.list.pageTexts.table.status.inActive")}
                  variant={item.is_active ? 'success' : 'danger'}
                />
              </td>
            )
          }

          if (headCell.id === VALIDATION_FIELD_MAPPING.customer) {
            return (
              <ElipsisCell
                key={headCell.id}
                width={headCell.width}
                node={customerNode}
              />
            )
          }

          if (headCell.id === VALIDATION_FIELD_MAPPING.managedBy) {
            return (
              <ElipsisCell
                key={headCell.id}
                width={headCell.width}
                node={managedByNode}
              />
            )
          }

          if (headCell.id === VALIDATION_FIELD_MAPPING.commissionPercentage) {
            return (
              <ElipsisCell
                key={headCell.id}
                width={headCell.width}
                text={typeof item?.commission_percentage !== 'undefined' && item?.commission_percentage !== null ? `${item.commission_percentage}%` : '-'}
              />
            )
          }

          if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
            return (
              <td key={headCell.id} width={headCell.width}>
                <div className="d-flex align-items-sm-center justify-content-sm-start">
                  {
                    hasEditAccess
                      ? <Button
                        className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                        icon={<i className="ri-edit-box-line ri-20px"></i>}
                        onClick={() => handleEditButtonClick(item.id)}
                      />
                      : <Button
                        className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                        icon={<i className="ri-eye-line ri-20px"></i>}
                        onClick={() => handleViewClick(item.id)}
                      />
                  }
                  <Tooltip
                    title={
                      <>
                        <div>{t('property.list.pageTexts.table.lastUpdatedBy')} {item.updater?.name || "-"}</div>
                        <div>{t('property.list.pageTexts.table.lastUpdated')} {item.updated_at || "-"}</div>
                      </>
                    }
                    arrow
                    classes={{
                      tooltip: 'tooltip',
                    }}
                  >
                    <i className="ri-timer-line ri-20px"></i>
                  </Tooltip>
                </div>
              </td>
            )
          }

          return (<ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] || '-'} />);
        })
      }
    </tr>
  );
}

export default Row;
